import { WifiOff } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useBreakpoint, useLocalStorage, useOnlineStatus } from '@/shared/lib/hooks';
import { cn } from '@/shared/lib/cn';
import { Drawer, OverflowTooltip } from '@/shared/ui';
import { MobileNav, Sidebar } from './Sidebar';
import { ShellProvider, useShell } from './ShellContext';
import { Topbar } from './Topbar';
import { WorkspaceProvider } from '@/domains/workspaces/WorkspaceProvider';

/**
 * The one application shell. Every page — including the editor — renders inside it.
 *
 * A workspace page (the editor) collapses the sidebar and takes over the scroll container,
 * but it does not get a second navigation, a second credit display, or a second session; it
 * portals its own context into this topbar's slot.
 */
export function AppShell() {
  return (
    <WorkspaceProvider>
      <ShellProvider>
        <ShellLayout />
      </ShellProvider>
    </WorkspaceProvider>
  );
}

function ShellLayout() {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useBreakpoint();
  const online = useOnlineStatus();
  const { chromeMode, setTopbarSlot } = useShell();
  const [collapsedPref, setCollapsedPref] = useLocalStorage('vgp_sidebar_collapsed', false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const slotRef = useRef<HTMLDivElement>(null);

  // Tablet always uses the icon rail; a workspace page collapses it too.
  const collapsed = isTablet || collapsedPref || chromeMode === 'workspace';
  const isWorkspace = chromeMode === 'workspace';

  useEffect(() => {
    setTopbarSlot(slotRef.current);
    return () => setTopbarSlot(null);
  }, [setTopbarSlot]);

  // Any navigation — a sidebar link, the workspace menu, the browser's back button — closes the
  // phone navigation drawer.
  const location = useLocation();
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex h-dvh w-full overflow-hidden bg-bg">
      <a
        href="#main"
        // Focus is moved explicitly rather than left to fragment navigation: the router owns
        // the URL, and relying on the browser's implicit behaviour left focus on the link in
        // practice, so the next Tab went back into the navigation instead of the content.
        onClick={(event) => {
          event.preventDefault();
          const main = document.getElementById('main');
          main?.focus();
          main?.scrollIntoView();
        }}
        className="sr-only-focusable absolute left-3 top-3 z-[100] rounded bg-accent px-3 py-2 text-sm font-medium text-accent-fg"
      >
        {t('nav.skipToContent')}
      </a>

      {!isMobile && (
        <aside className="shrink-0">
          <Sidebar
            collapsed={collapsed}
            onToggle={() => setCollapsedPref(!collapsedPref)}
            hideToggle={isWorkspace}
          />
        </aside>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* No hamburger on a workspace page: the drawer it opens is not rendered there, so on the
            editor it was a button that did nothing — and it cost the room the editor's own
            header needs on a phone. */}
        <Topbar
          onOpenMobileNav={isMobile && !isWorkspace ? () => setMobileNavOpen(true) : undefined}
          compact={isMobile && isWorkspace}
        >
          <div ref={slotRef} className="flex min-w-0 flex-1 items-center gap-2" />
        </Topbar>

        {!online && (
          <div
            className="flex items-center gap-2 border-b border-warning/30 bg-warning-subtle px-4 py-1.5 text-sm text-warning"
            role="status"
          >
            <WifiOff className="h-3.5 w-3.5 shrink-0" />
            {t('states.offline')} — {t('states.offlineBody')}
          </div>
        )}

        <main
          id="main"
          // Programmatically focusable so the skip link actually moves focus here, not just
          // the scroll position. -1 keeps it out of the normal tab order.
          tabIndex={-1}
          className={cn(
            'min-h-0 flex-1',
            // A workspace page owns its own scrolling; everything else scrolls here.
            isWorkspace ? 'overflow-hidden' : 'overflow-y-auto',
            isMobile && !isWorkspace && 'pb-16',
            'focus:outline-none',
          )}
        >
          <Outlet />
        </main>
      </div>

      {isMobile && !isWorkspace && (
        <>
          <MobileNav />
          <Drawer open={mobileNavOpen} onOpenChange={setMobileNavOpen} title={t('a11y.mainNavigation')}>
            {/* Not wrapped in a <button> any more: the sidebar holds links and the workspace
                switcher's own button, and interactive content inside a button is invalid — the
                switcher could not even open, because the click closed the drawer first. The
                drawer closes on navigation instead (the effect above). */}
            <Sidebar collapsed={false} onToggle={() => {}} hideToggle embedded />
          </Drawer>
        </>
      )}
    </div>
  );
}

/** A page header with a consistent title/description/actions layout. */
export function PageHeader({
  title,
  description,
  actions,
  className,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-start justify-between gap-3', className)}>
      <div className="min-w-0">
        {/* Titles can be user data — a pasted platform caption is a paragraph, not a name. Clamp
            it so it cannot push the action row off the page; the full string stays on hover. */}
        <OverflowTooltip content={title} side="bottom">
          <h1 className="line-clamp-2 break-words text-xl font-semibold tracking-tight">{title}</h1>
        </OverflowTooltip>
        {description && <p className="mt-1 text-base text-fg-muted">{description}</p>}
      </div>
      {/* max-w-full, not shrink-0 alone: on a phone the row wraps under the title, and a
          non-shrinking row of three buttons ran off the right edge — the overflow menu with
          Rename/Delete was unreachable. Now the buttons wrap inside the screen instead. */}
      {actions && <div className="flex max-w-full flex-wrap items-center gap-2 sm:shrink-0">{actions}</div>}
    </div>
  );
}

/**
 * Standard page padding. Dense by design — the design system is "data-dense dashboard".
 *
 * One width for every page, wide enough to fill a 1920 px screen beside the sidebar. Most pages
 * used to stop at 1280 px, which on such a screen left 400 px of nothing; only a few were let
 * out. The cap is still there for wider screens than that, where a line would get too long.
 */
export function PageContainer({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('mx-auto w-full max-w-[110rem] px-4 py-5 md:px-6', className)}>
      {children}
    </div>
  );
}
